import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import apiFeedbacks, { IFeedback, IFeedbackList } from 'api/feedbacks';
import PageContainer from 'components/layout/PageContainer';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import translations from 'services/translations/translations';

import { FEEDBACK_MOODS } from './FeedbackButton';
import FeedbackResponse, { FEEDBACK_STATUSES } from './FeedbackResponse';
import {
  IFeedbackFilters,
  NO_FILTERS,
  RATING_RANGES,
  filterFeedbacks,
  giverOf,
  isFiltered,
} from './feedbackFilters';

import 'style/feedback/Feedback.css';

const emojiOf = (mood: string) =>
  FEEDBACK_MOODS.find((choice) => choice.mood === mood)?.emoji ?? mood;

// A value of a CSV row for Excel: in quotes, the quotes doubled
const csvValue = (value: string) => `"${value.replace(/"/g, '""')}"`;

// The weeks shown in the table of the weekly averages
const WEEKS_SHOWN = 12;

// Every column of the list can be sorted by
type SortKey =
  | 'created'
  | 'giver'
  | 'mood'
  | 'page'
  | 'rating'
  | 'status'
  | 'text';

interface ISort {
  ascending: boolean;
  key: SortKey;
}

// The quick choices above the list: the newest first, or by the status
const NEWEST_FIRST: ISort = { ascending: false, key: 'created' };
const BY_STATUS: ISort = { ascending: true, key: 'status' };

// The order of the statuses: not handled first, then in progress, done, not done
const STATUS_ORDER = [null, 'in-progress', 'done', 'not-done'];

const MOOD_ORDER = ['negative', 'neutral', 'positive'];

const average = (value: null | number) =>
  value === null ? '–' : formatGroupedNumber(value, 1, 1);

/**
 * The feedback of the users, newest first or by the status the development has given it,
 * with the averages of the overall ratings of all pages, of each page and of each week.
 */
const FeedbackList = (): JSX.Element => {
  const { t, i18n } = useTranslation([translations.common]);
  const tr = (key: string, options?: Record<string, unknown>) =>
    t(`${translations.common}:feedback.${key}`, options);
  const [list, setList] = useState<IFeedbackList | null>(null);
  const [sort, setSort] = useState<ISort>(NEWEST_FIRST);

  useEffect(() => {
    let cancelled = false;
    apiFeedbacks
      .get()
      .then((result) => !cancelled && setList(result))
      .catch(() => !cancelled && setList(null));
    return () => {
      cancelled = true;
    };
  }, []);

  const feedbacks = list?.feedbacks ?? [];

  // The search: only the feedback that matches is shown, sorted and exported
  const [filters, setFilters] = useState<IFeedbackFilters>(NO_FILTERS);
  const setFilter = (key: keyof IFeedbackFilters, value: string) =>
    setFilters({ ...filters, [key]: value });
  const filtered = isFiltered(filters);
  const shown = filterFeedbacks(feedbacks, filters);

  // The choices of the filters: the values in the list
  const distinct = (values: string[]) =>
    Array.from(new Set(values)).sort((a, b) =>
      a.localeCompare(b, i18n.language)
    );
  const pageChoices = distinct(feedbacks.map((feedback) => feedback.page));
  const giverChoices = distinct(feedbacks.map(giverOf));
  const departmentChoices = distinct(
    feedbacks
      .map((feedback) => feedback.departmentName ?? '')
      .filter((department) => department !== '')
  );

  const choice = (key: keyof IFeedbackFilters, options: [string, string][]) => (
    <select
      aria-label={key}
      className="form-control"
      onChange={(event) => setFilter(key, event.target.value)}
      value={filters[key]}
    >
      {options.map(([value, label]) => (
        <option key={value} value={value}>
          {label}
        </option>
      ))}
    </select>
  );

  // The value of the sorted column; a feedback without a rating comes last either way
  const compare = (a: IFeedback, b: IFeedback): number => {
    switch (sort.key) {
      case 'giver':
        return giverOf(a).localeCompare(giverOf(b), i18n.language);
      case 'mood':
        return MOOD_ORDER.indexOf(a.mood) - MOOD_ORDER.indexOf(b.mood);
      case 'page':
        return `${a.page} ${a.location ?? ''}`.localeCompare(
          `${b.page} ${b.location ?? ''}`,
          i18n.language
        );
      case 'status':
        return STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status);
      case 'text':
        return a.text.localeCompare(b.text, i18n.language);
      default:
        return a.createdAt.localeCompare(b.createdAt);
    }
  };

  const sorted = [...shown].sort((a, b) => {
    if (sort.key === 'rating' && (a.rating === null) !== (b.rating === null)) {
      return a.rating === null ? 1 : -1;
    }
    const order =
      sort.key === 'rating' ? (a.rating ?? 0) - (b.rating ?? 0) : compare(a, b);
    // The same value: the newest first
    return (
      (sort.ascending ? order : -order) ||
      b.createdAt.localeCompare(a.createdAt)
    );
  });

  // A click on the heading sorts by it, a second click the other way
  const sortBy = (key: SortKey) =>
    setSort(
      sort.key === key
        ? { ascending: !sort.ascending, key }
        : { ascending: key !== 'created', key }
    );

  // The heading of a column: the sorted one shows its direction
  const heading = (key: SortKey, label: string) => {
    const isSorted = sort.key === key;
    const direction = sort.ascending ? 'ascending' : 'descending';
    const arrow = sort.ascending ? ' ▲' : ' ▼';

    return (
      <th aria-sort={isSorted ? direction : 'none'}>
        <button
          className="feedback-sort-heading"
          onClick={() => sortBy(key)}
          type="button"
        >
          {label}
          <span aria-hidden="true">{isSorted ? arrow : ' ↕'}</span>
        </button>
      </th>
    );
  };

  const replace = (changed: IFeedback) =>
    setList(
      list === null
        ? null
        : {
            ...list,
            feedbacks: list.feedbacks.map((feedback) =>
              feedback.id === changed.id ? changed : feedback
            ),
          }
    );

  const formatTime = (value: string) =>
    new Date(value).toLocaleString(i18n.language, {
      dateStyle: 'short',
      timeStyle: 'short',
    });

  const statusOf = (feedback: IFeedback) =>
    feedback.status ? tr(`statuses.${feedback.status}`) : tr('statuses.new');

  // Semicolons and a byte order mark, so Excel opens the file in columns with å, ä and ö
  const exportCsv = () => {
    const rows = [
      [
        tr('Time'),
        tr('Page'),
        tr('Location'),
        tr('Giver'),
        tr('Department'),
        tr('Mood'),
        tr('Rating'),
        tr('Message'),
        tr('Status'),
        tr('Response'),
      ],
      ...sorted.map((feedback) => [
        formatTime(feedback.createdAt),
        feedback.page,
        feedback.location ?? '',
        giverOf(feedback),
        feedback.departmentName ?? '',
        tr(`moods.${feedback.mood}`),
        feedback.rating === null ? '' : String(feedback.rating),
        feedback.text,
        statusOf(feedback),
        feedback.response ?? '',
      ]),
    ];
    const csv = rows.map((row) => row.map(csvValue).join(';')).join('\r\n');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(
      new Blob([String.fromCharCode(0xfeff), csv], {
        type: 'text/csv;charset=utf-8',
      })
    );
    link.download = `palautteet-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const pages = list?.pages.map((page) => page.page) ?? [];
  const weeks = list?.weeks.slice(0, WEEKS_SHOWN) ?? [];

  return (
    <PageContainer fullWidth>
      <div className="feedback-list">
        <div className="feedback-list-header">
          <div>
            <h1>{tr('List title')}</h1>
            <p>
              {filtered
                ? tr('Shown', { count: shown.length, total: feedbacks.length })
                : tr('Count', { count: feedbacks.length })}
            </p>
            {/* The search of all the texts of the feedback */}
            <div className="feedback-search">
              <input
                aria-label={tr('Search')}
                className="form-control"
                onChange={(event) => setFilter('search', event.target.value)}
                placeholder={tr('Search placeholder')}
                type="search"
                value={filters.search}
              />
              {/* The department of the givers */}
              <div className="feedback-department">
                {choice('department', [
                  ['', tr('All departments')],
                  ...departmentChoices.map((department): [string, string] => [
                    department,
                    department,
                  ]),
                ])}
              </div>
              {filtered ? (
                <button
                  className="feedback-clear-filters"
                  onClick={() => setFilters(NO_FILTERS)}
                  type="button"
                >
                  {tr('Clear filters')}
                </button>
              ) : null}
            </div>
            <div className="feedback-list-actions">
              <span>{tr('Sort')}</span>
              <div className="feedback-sort">
                {[
                  { choice: NEWEST_FIRST, key: 'created' },
                  { choice: BY_STATUS, key: 'status' },
                ].map(({ choice, key }) => (
                  <button
                    className={
                      sort.key === choice.key &&
                      sort.ascending === choice.ascending
                        ? 'active'
                        : undefined
                    }
                    key={key}
                    onClick={() => setSort(choice)}
                    type="button"
                  >
                    {tr(`sort.${key}`)}
                  </button>
                ))}
              </div>
              {feedbacks.length > 0 ? (
                <PrimaryButton onClick={exportCsv}>
                  {tr('Export')}
                </PrimaryButton>
              ) : null}
            </div>
          </div>

          {/* The averages of the overall ratings at the right edge */}
          {list === null ? null : (
            <div className="feedback-summary">
              <div className="feedback-average">
                <strong>{average(list.average)}</strong>
                <span>/ 10</span>
              </div>
              <p className="feedback-average-label">
                {tr('Overall average', { count: list.ratingCount })}
              </p>
              {list.pages.map((page) => (
                <p className="feedback-page-average" key={page.page}>
                  <span>{page.page}</span>
                  <strong>{average(page.average)}</strong>
                  <small>{tr('Ratings', { count: page.count })}</small>
                </p>
              ))}
              <p className="feedback-users">
                {tr('Users', { count: list.userCount })}
                <br />
                {tr('Givers', { count: list.giverCount })}
              </p>
            </div>
          )}
        </div>

        {/* How the ratings develop: the averages of the last weeks */}
        {weeks.length > 0 ? (
          <div className="feedback-weeks">
            <h2>{tr('Weekly averages')}</h2>
            <table>
              <thead>
                <tr>
                  <th>{tr('Week')}</th>
                  <th>{tr('All pages')}</th>
                  {pages.map((page) => (
                    <th key={page}>{page}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {weeks.map((week) => (
                  <tr key={week.starts}>
                    <td>{`${tr('Week short')} ${week.week}/${week.year}`}</td>
                    <td>
                      <strong>{average(week.average)}</strong>
                      <small>{` (${week.count})`}</small>
                    </td>
                    {pages.map((page) => {
                      const ofPage = week.pages.find(
                        (weekPage) => weekPage.page === page
                      );
                      return (
                        <td key={page}>
                          {ofPage ? (
                            <>
                              {average(ofPage.average)}
                              <small>{` (${ofPage.count})`}</small>
                            </>
                          ) : (
                            '–'
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {list !== null && feedbacks.length === 0 ? <p>{tr('Empty')}</p> : null}
        {feedbacks.length > 0 ? (
          <table className="feedback-table">
            <thead>
              <tr>
                {heading('created', tr('Time'))}
                {heading('page', tr('Page'))}
                {heading('giver', tr('Giver'))}
                {heading('mood', tr('Mood'))}
                {heading('rating', tr('Rating'))}
                {heading('text', tr('Message'))}
                {/* The response by its status */}
                {heading('status', tr('Response'))}
              </tr>
              {/* The filter of each column */}
              <tr className="feedback-filters">
                <th>
                  {/* From and to side by side */}
                  <div className="feedback-dates">
                    <input
                      aria-label={tr('From')}
                      className="form-control"
                      onChange={(event) =>
                        setFilter('from', event.target.value)
                      }
                      title={tr('From')}
                      type="date"
                      value={filters.from}
                    />
                    <span aria-hidden="true">–</span>
                    <input
                      aria-label={tr('To')}
                      className="form-control"
                      onChange={(event) => setFilter('to', event.target.value)}
                      title={tr('To')}
                      type="date"
                      value={filters.to}
                    />
                  </div>
                </th>
                <th>
                  {choice('page', [
                    ['', tr('All')],
                    ...pageChoices.map((page): [string, string] => [
                      page,
                      page,
                    ]),
                  ])}
                </th>
                <th>
                  {choice('giver', [
                    ['', tr('All')],
                    ...giverChoices.map((giver): [string, string] => [
                      giver,
                      giver,
                    ]),
                  ])}
                </th>
                <th>
                  {choice('mood', [
                    ['', tr('All')],
                    ...FEEDBACK_MOODS.map((mood): [string, string] => [
                      mood.mood,
                      `${mood.emoji} ${tr(`moods.${mood.mood}`)}`,
                    ]),
                  ])}
                </th>
                <th>
                  {choice('rating', [
                    ['', tr('All')],
                    ...RATING_RANGES.map((range): [string, string] => [
                      range,
                      range.replace('-', '–'),
                    ]),
                    ['none', tr('No rating')],
                  ])}
                </th>
                <th>
                  <input
                    aria-label={tr('Message')}
                    className="form-control"
                    onChange={(event) => setFilter('text', event.target.value)}
                    placeholder={tr('Contains')}
                    type="search"
                    value={filters.text}
                  />
                </th>
                <th>
                  {choice('status', [
                    ['', tr('All')],
                    ['new', tr('statuses.new')],
                    ...FEEDBACK_STATUSES.map((status): [string, string] => [
                      status,
                      tr(`statuses.${status}`),
                    ]),
                  ])}
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 ? (
                <tr>
                  <td colSpan={7}>{tr('No matches')}</td>
                </tr>
              ) : null}
              {sorted.map((feedback) => (
                <tr key={feedback.id}>
                  <td>{formatTime(feedback.createdAt)}</td>
                  <td>
                    {feedback.page}
                    {feedback.location ? (
                      <small className="feedback-location">
                        {feedback.location}
                      </small>
                    ) : null}
                  </td>
                  <td title={feedback.userEmail ?? undefined}>
                    {giverOf(feedback)}
                    {feedback.departmentName ? (
                      <small className="feedback-location">
                        {feedback.departmentName}
                      </small>
                    ) : null}
                  </td>
                  <td
                    className="feedback-mood"
                    title={tr(`moods.${feedback.mood}`)}
                  >
                    {emojiOf(feedback.mood)}
                  </td>
                  <td className="feedback-rating">{feedback.rating ?? '–'}</td>
                  <td className="feedback-text">{feedback.text || '–'}</td>
                  <td>
                    <FeedbackResponse
                      canRespond={list?.canRespond ?? false}
                      feedback={feedback}
                      onSaved={replace}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </div>
    </PageContainer>
  );
};

export default FeedbackList;
